import { useState, useEffect } from "react";
import { Link } from "react-router";
import { useAuthStore } from "../../stores/auth.store";
import {
  getComments,
  createComment,
  deleteComment,
  type CommentWithUser,
} from "../../api/post.api";

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = now - then;
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d`;
  const months = Math.floor(days / 30);
  return `${months}mo`;
}

interface Props {
  postId: string;
  commentCount: number;
  onCountChange: (delta: number) => void;
}

export function CommentSection({ postId, commentCount, onCountChange }: Props) {
  const user = useAuthStore((s) => s.user);
  const [commentList, setCommentList] = useState<CommentWithUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadComments(1);
  }, [postId]);

  async function loadComments(p: number) {
    try {
      const data = await getComments(postId, p);
      if (p === 1) {
        setCommentList(data.comments);
      } else {
        setCommentList((prev) => [...prev, ...data.comments]);
      }
      setPage(p);
      setHasMore(data.comments.length === data.limit);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!newComment.trim() || submitting) return;

    setSubmitting(true);
    try {
      const data = await createComment(postId, newComment.trim());
      setCommentList((prev) => [...prev, data.comment]);
      setNewComment("");
      onCountChange(1);
    } catch {
      // silently fail
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(commentId: string) {
    try {
      await deleteComment(postId, commentId);
      setCommentList((prev) => prev.filter((c) => c.id !== commentId));
      onCountChange(-1);
    } catch {
      // silently fail
    }
  }

  if (loading) {
    return (
      <div className="px-4 py-3">
        <div className="h-4 w-32 animate-pulse rounded bg-gray-200" />
      </div>
    );
  }

  return (
    <div className="px-4 pb-4">
      {/* Comment count header */}
      {commentCount > 0 && (
        <p className="mb-3 text-sm font-medium text-gray-500">
          {commentCount} {commentCount === 1 ? "comment" : "comments"}
        </p>
      )}

      {/* Comment list */}
      <div className="space-y-3">
        {commentList.map((comment) => (
          <div key={comment.id} className="flex gap-2">
            {/* Avatar */}
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-200 text-xs font-medium text-gray-600">
              {comment.userAvatarUrl ? (
                <img
                  src={comment.userAvatarUrl}
                  alt=""
                  className="h-full w-full rounded-full object-cover"
                />
              ) : (
                comment.userName.charAt(0).toUpperCase()
              )}
            </div>

            {/* Body */}
            <div className="min-w-0 flex-1">
              <p className="text-sm text-gray-900">
                <span className="font-semibold">{comment.userName}</span>{" "}
                {comment.body}
              </p>
              <div className="mt-0.5 flex items-center gap-2 text-xs text-gray-400">
                <span>{timeAgo(comment.createdAt)}</span>
                {user && user.id === comment.userId && (
                  <button
                    onClick={() => handleDelete(comment.id)}
                    aria-label={`Delete comment by ${comment.userName}`}
                    className="text-red-400 hover:text-red-600 focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 rounded"
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Load more */}
      {hasMore && (
        <button
          onClick={() => loadComments(page + 1)}
          className="mt-3 text-sm font-medium text-gray-500 hover:text-gray-700"
        >
          Load more comments
        </button>
      )}

      {/* Add comment form */}
      {user ? (
        <form onSubmit={handleSubmit} className="mt-3 flex gap-2">
          <input
            type="text"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Add a comment..."
            maxLength={1000}
            className="min-w-0 flex-1 rounded-full border border-gray-300 px-4 py-2 text-sm outline-none focus:border-gray-500"
          />
          <button
            type="submit"
            disabled={!newComment.trim() || submitting}
            aria-label="Post comment"
            className="text-sm font-semibold text-blue-500 disabled:opacity-40 focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 rounded"
          >
            Post
          </button>
        </form>
      ) : (
        <p className="mt-3 text-sm text-gray-400">
          <Link to="/login" className="font-medium text-blue-500 hover:underline">
            Log in
          </Link>{" "}
          to comment
        </p>
      )}
    </div>
  );
}
